import React, { useCallback, useEffect, useState } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { BookOpen, ChevronDown, ChevronUp, FileText, Loader2, Sparkles, Send } from 'lucide-react';
import AssignToClassModal from './AssignToClassModal';
import { useSubjects } from '../hooks/useSubjects';

/**
 * Question Bank — browse verbatim past paper questions extracted from
 * uploaded Edexcel mark schemes. 3-level filter: Subject → Topic → Marks.
 */
export default function QuestionBankTab() {
    const { subjects: subjectsList } = useSubjects();
    const subjectNames = subjectsList.map((s) => s.name);
    const [subject, setSubject] = useState('Business');
    const [topics, setTopics] = useState([]);
    const [loadingTopics, setLoadingTopics] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);

    const [marks, setMarks] = useState([]);
    const [loadingMarks, setLoadingMarks] = useState(false);
    const [selectedMarks, setSelectedMarks] = useState(null);

    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    const [stats, setStats] = useState(null);

    // Load stats once (used for the "extracting" banner)
    useEffect(() => {
        api.get('/api/questions-bank/stats').then((r) => setStats(r.data)).catch(() => {});
    }, []);

    // When subject changes, reload topics and clear downstream state
    const loadTopics = useCallback(async (subj) => {
        setLoadingTopics(true);
        setTopics([]);
        setSelectedTopic(null);
        setMarks([]);
        setSelectedMarks(null);
        setQuestions([]);
        try {
            const r = await api.get(`/api/questions-bank/topics?subject=${encodeURIComponent(subj)}`);
            setTopics(r.data.topics || []);
        } finally {
            setLoadingTopics(false);
        }
    }, []);

    useEffect(() => {
        loadTopics(subject);
    }, [subject, loadTopics]);

    // When topic changes, reload marks
    useEffect(() => {
        if (!selectedTopic) {
            setMarks([]);
            setSelectedMarks(null);
            setQuestions([]);
            return;
        }
        setLoadingMarks(true);
        setSelectedMarks(null);
        setQuestions([]);
        api
            .get(`/api/questions-bank/marks?subject=${encodeURIComponent(subject)}&topic=${encodeURIComponent(selectedTopic)}`)
            .then((r) => setMarks(r.data.marks || []))
            .finally(() => setLoadingMarks(false));
    }, [selectedTopic, subject]);

    // When marks change, load questions
    useEffect(() => {
        if (!selectedTopic || !selectedMarks) {
            setQuestions([]);
            return;
        }
        setLoadingQuestions(true);
        api
            .get(
                `/api/questions-bank/questions?subject=${encodeURIComponent(subject)}&topic=${encodeURIComponent(selectedTopic)}&marks=${selectedMarks}`
            )
            .then((r) => setQuestions(r.data.items || []))
            .finally(() => setLoadingQuestions(false));
    }, [selectedMarks, selectedTopic, subject]);

    return (
        <div className="space-y-6 fade-in" data-testid="question-bank-tab">
            {/* Header */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-black text-zinc-950">Question Bank</h1>
                <p className="text-sm text-zinc-500 mt-1">
                    Browse verbatim past paper questions from Pearson Edexcel mark schemes. Filter by subject, topic and marks.
                </p>
            </div>

            {/* Extraction status banner */}
            {stats && stats.total < 50 && (
                <div
                    className="flex items-start gap-3 p-4 rounded-2xl border border-[#EDE4FF] bg-[#F8F4FF]"
                    data-testid="qb-extracting-banner"
                >
                    <Loader2 className="w-5 h-5 text-[#7C3AED] animate-spin flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-bold text-zinc-950">Extracting questions in the background…</p>
                        <p className="text-xs text-zinc-600 mt-1">
                            We're parsing every uploaded mark scheme to pull out verbatim questions and model answers. This
                            takes a few minutes on first boot. Refresh the page in a bit to see new questions appear.
                            {stats.total > 0 ? ` (${stats.total} extracted so far)` : ''}
                        </p>
                    </div>
                </div>
            )}

            {/* SUBJECT */}
            <Section step="1" title="Choose a subject">
                <div className="flex flex-wrap gap-2" data-testid="qb-subjects">
                    {subjectNames.map((s) => (
                        <button
                            key={s}
                            onClick={() => setSubject(s)}
                            className={`px-5 py-2.5 text-sm font-bold rounded-lg transition-all ${
                                subject === s
                                    ? 'bg-[#7C3AED] text-white shadow-[0_4px_14px_-4px_rgba(124,58,237,0.5)]'
                                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                            data-testid={`qb-subject-${s.toLowerCase().replace(/\s+/g, '_')}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </Section>

            {/* TOPIC */}
            <Section step="2" title="Choose a topic" muted={!subject}>
                {loadingTopics ? (
                    <SkeletonRow />
                ) : topics.length === 0 ? (
                    <p className="text-sm text-zinc-400">No topics available.</p>
                ) : (
                    <div className="flex flex-wrap gap-2" data-testid="qb-topics">
                        {topics.map((t) => {
                            const active = selectedTopic === t.topic;
                            const empty = t.count === 0;
                            return (
                                <button
                                    key={t.topic}
                                    onClick={() => setSelectedTopic(t.topic)}
                                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg transition-all ${
                                        active
                                            ? 'bg-[#7C3AED] text-white shadow-[0_4px_14px_-4px_rgba(124,58,237,0.5)]'
                                            : empty
                                            ? 'bg-zinc-50 text-zinc-400 hover:bg-zinc-100'
                                            : 'bg-white border border-zinc-200 text-zinc-700 hover:border-[#7C3AED] hover:text-[#7C3AED]'
                                    }`}
                                    data-testid={`qb-topic-${t.topic.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    {t.topic}
                                    <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${
                                            active ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-500'
                                        }`}
                                    >
                                        {t.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </Section>

            {/* MARKS */}
            <Section step="3" title="Choose marks" muted={!selectedTopic}>
                {!selectedTopic ? (
                    <p className="text-sm text-zinc-400">Pick a topic first.</p>
                ) : loadingMarks ? (
                    <SkeletonRow />
                ) : marks.length === 0 ? (
                    <p className="text-sm text-zinc-500" data-testid="qb-no-questions-topic">
                        No past paper questions available for this topic yet.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2" data-testid="qb-marks">
                        {marks.map((m) => (
                            <button
                                key={m.marks}
                                onClick={() => setSelectedMarks(m.marks)}
                                className={`px-4 py-2.5 text-sm font-bold rounded-lg transition-all ${
                                    selectedMarks === m.marks
                                        ? 'bg-[#7C3AED] text-white shadow-[0_4px_14px_-4px_rgba(124,58,237,0.5)]'
                                        : 'bg-white border border-zinc-200 text-zinc-700 hover:border-[#7C3AED] hover:text-[#7C3AED]'
                                }`}
                                data-testid={`qb-marks-${m.marks}`}
                            >
                                {m.marks} marks
                                <span
                                    className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-black ${
                                        selectedMarks === m.marks ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-500'
                                    }`}
                                >
                                    {m.count}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </Section>

            {/* RESULTS */}
            {selectedTopic && selectedMarks && (
                <div className="space-y-3" data-testid="qb-results">
                    <div className="flex items-center justify-between">
                        <h3 className="text-base font-black text-zinc-950">
                            {questions.length} question{questions.length === 1 ? '' : 's'}
                        </h3>
                        <span className="text-xs text-zinc-500">
                            {subject} · {selectedTopic} · {selectedMarks} marks
                        </span>
                    </div>
                    {loadingQuestions ? (
                        <>
                            <SkeletonCard />
                            <SkeletonCard />
                        </>
                    ) : questions.length === 0 ? (
                        <EmptyResults />
                    ) : (
                        questions.map((q, i) => <QuestionCard key={i} q={q} idx={i} subject={subject} />)
                    )}
                </div>
            )}
        </div>
    );
}

// ---------------- Sub-components ----------------

function Section({ step, title, muted = false, children }) {
    return (
        <div
            className={`bg-white rounded-2xl border border-zinc-100 p-5 shadow-[0_2px_10px_-3px_rgba(124,58,237,0.08)] transition-opacity ${
                muted ? 'opacity-60' : 'opacity-100'
            }`}
        >
            <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 rounded-full bg-[#7C3AED] text-white text-xs font-black flex items-center justify-center">
                    {step}
                </span>
                <h2 className="text-base font-black text-zinc-950">{title}</h2>
            </div>
            {children}
        </div>
    );
}

function SkeletonRow() {
    return (
        <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-9 w-24 bg-zinc-100 rounded-lg animate-pulse" />
            ))}
        </div>
    );
}

function SkeletonCard() {
    return <div className="h-32 bg-zinc-100 rounded-2xl animate-pulse" />;
}

function EmptyResults() {
    return (
        <div className="bg-white rounded-2xl border border-zinc-100 p-8 text-center" data-testid="qb-empty-results">
            <div className="w-12 h-12 rounded-full bg-[#F4ECFF] mx-auto flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-[#7C3AED]" />
            </div>
            <h3 className="text-base font-bold text-zinc-950 mb-1">No questions match</h3>
            <p className="text-sm text-zinc-500 max-w-md mx-auto">
                Try a different mark value or topic.
            </p>
        </div>
    );
}

function QuestionCard({ q, idx, subject }) {
    const { user } = useAuth();
    const isTeacher = user?.role === 'teacher';
    const [open, setOpen] = useState(false);
    const [showAssign, setShowAssign] = useState(false);
    return (
        <div
            className="bg-white rounded-2xl border border-zinc-100 p-5 shadow-[0_2px_10px_-3px_rgba(124,58,237,0.08)]"
            data-testid={`qb-question-card-${idx}`}
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    {q.question_number && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#F4ECFF] text-[#7C3AED] uppercase tracking-wider">
                            Q {q.question_number}
                        </span>
                    )}
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 uppercase tracking-wider">
                        {q.marks} marks
                    </span>
                    <span className="text-[11px] text-zinc-400">{q.source}{q.unit ? ` · ${q.unit}` : ''}</span>
                </div>
                <FileText className="w-4 h-4 text-zinc-300 flex-shrink-0 mt-1" />
            </div>

            <p className="text-sm text-zinc-900 leading-relaxed whitespace-pre-wrap">{q.question_text}</p>

            <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                    onClick={() => setOpen((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#7C3AED] hover:text-[#6D28D9]"
                    data-testid={`qb-ms-toggle-${idx}`}
                >
                    {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {open ? 'Hide' : 'Show'} mark scheme answer
                </button>
                {isTeacher && (
                    <button
                        onClick={() => setShowAssign(true)}
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold bg-[#7C3AED] text-white px-3 py-1.5 rounded-lg hover:bg-[#6D28D9]"
                        data-testid={`qb-assign-${idx}`}
                    >
                        <Send className="w-3.5 h-3.5" /> Assign to class
                    </button>
                )}
            </div>

            {open && (
                <div className="mt-3 p-4 rounded-xl bg-zinc-50 border border-zinc-100" data-testid={`qb-ms-answer-${idx}`}>
                    <div className="flex items-center gap-1.5 mb-2">
                        <BookOpen className="w-3.5 h-3.5 text-[#7C3AED]" />
                        <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Mark scheme answer</span>
                    </div>
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{q.mark_scheme_answer}</p>
                </div>
            )}

            {showAssign && (
                <AssignToClassModal
                    source="question_bank"
                    subject={subject}
                    level="IAL"
                    title={`${subject}${q.question_number ? ` · Q${q.question_number}` : ''}${q.marks ? ` · ${q.marks} marks` : ''}`}
                    question_text={q.question_text}
                    mark_scheme={q.mark_scheme_answer}
                    marks={q.marks}
                    paper_year={q.paper_year || ''}
                    series={q.series || ''}
                    question_number={q.question_number || ''}
                    onClose={() => setShowAssign(false)}
                />
            )}
        </div>
    );
}
